import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Camera,
  Globe,
  LogOut,
  Menu,
  RefreshCw,
  UserCircle2,
  UserPen,
} from 'lucide-react';
import { getCaseById, clientInfo } from '../../data/mockCases';
import { getUser, logout } from '../../utils/auth';
import CaseHeader from '../../components/client/case/CaseHeader';
import CaseMainPanel from '../../components/client/case/CaseMainPanel';
import CaseSidebar from '../../components/client/case/CaseSidebar';

const PALETTE = {
  page: '#F7F9FB',
  navy: '#0F2742',
  green: '#1F8A70',
  gold: '#C8A45C',
  border: '#DCE7E3',
};

const hasMojibake = (value = '') => /[ØÙطأ™\uFFFD]/.test(String(value));

const safeValue = (value, fallback) => {
  if (!value || hasMojibake(value)) {
    return fallback;
  }

  return value;
};

const CaseDetailsPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [caseData, setCaseData] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const contentRef = useRef(null);
  const currentUser = useMemo(() => getUser(), []);
  const isArabic = i18n.language !== 'en';

  useEffect(() => {
    setCaseData(getCaseById(caseId));
  }, [caseId]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const displayName = safeValue(
    currentUser?.full_name || clientInfo?.name,
    isArabic ? 'أحمد بن عبدالله السالم' : 'Ahmad Bin Abdullah Al Salem'
  );

  const canSwitchAccount =
    Array.isArray(currentUser?.available_roles) &&
    currentUser.available_roles.length > 1;

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!caseData) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: PALETTE.page }}
      >
        <div className="text-center bg-white rounded-3xl shadow-lg border border-slate-200 px-8 py-10">
          <h2 className="text-2xl font-black mb-3" style={{ color: PALETTE.navy }}>
            {isArabic ? 'تعذر العثور على ملف القضية' : 'Case file could not be found'}
          </h2>
          <p className="text-slate-600 mb-6">
            {isArabic
              ? 'قد يكون رقم الملف غير صحيح أو تم نقله إلى قسم آخر.'
              : 'The file number may be invalid or moved.'}
          </p>
          <button
            onClick={() => navigate('/client/dashboard')}
            className="px-6 py-3 rounded-2xl text-white font-bold shadow-md transition-transform hover:-translate-y-0.5"
            style={{ background: `linear-gradient(135deg, ${PALETTE.green}, #2AA586)` }}
          >
            {isArabic ? 'العودة إلى لوحة العميل' : 'Back to Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: PALETTE.page }}>
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <img
          src="/images/wazin-mark.png"
          alt=""
          className="absolute top-28 -left-20 w-[520px] max-w-none select-none"
          style={{ opacity: 0.045, filter: 'grayscale(100%) brightness(70%)' }}
        />
        <img
          src="/images/wazin-mark.png"
          alt=""
          className="absolute bottom-20 -right-16 w-[460px] max-w-none select-none"
          style={{ opacity: 0.04, filter: 'grayscale(100%) brightness(75%)' }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <header
          className="relative overflow-visible rounded-[30px] border border-white/40 shadow-[0_24px_60px_rgba(15,39,66,0.16)] text-white"
          style={{
            background: `linear-gradient(135deg, ${PALETTE.green} 0%, #238D74 48%, ${PALETTE.navy} 100%)`,
          }}
        >
          <img
            src="/images/wazin-mark.png"
            alt=""
            className="absolute left-1/2 top-1/2 w-[340px] -translate-x-1/2 -translate-y-1/2 select-none"
            style={{ opacity: 0.07, filter: 'brightness(0) invert(1)' }}
          />

          <div className="relative z-20 px-6 py-5 sm:px-8">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-4">
                <button
                  onClick={() => navigate('/client/dashboard')}
                  className="mt-1 inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white transition-all hover:bg-white/16"
                  aria-label={isArabic ? 'العودة' : 'Back'}
                >
                  <ArrowRight className={`h-5 w-5 ${isArabic ? '' : 'rotate-180'}`} />
                </button>

                <div>
                  <div className="mb-2 flex flex-wrap items-center gap-3">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-bold text-white/95 backdrop-blur">
                      <img
                        src="/images/wazin-mark.png"
                        alt=""
                        className="h-5 w-5 object-contain invert"
                      />
                      <span>{isArabic ? 'منصة وازن القانونية' : 'Wazin Legal Platform'}</span>
                    </div>
                  </div>

                  {!isArabic && (
                    <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white">
                      Case Details
                    </h1>
                  )}

                  <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-white/85">
                    <span>{displayName}</span>
                    <span className="hidden sm:inline text-white/40">•</span>
                    <span>
                      {isArabic ? 'رقم القضية:' : 'Case No:'} {caseData.caseNumber}
                    </span>
                  </div>

                  <p className="mt-3 max-w-3xl text-sm sm:text-base leading-7 text-white/90">
                    {isArabic
                      ? 'هنا تجد آخر مستجدات ملفك القانوني، ومساره الإجرائي، والمرفقات والخطوات المرتبطة به بوضوح.'
                      : 'Here you can review your legal file updates, procedural path, attachments, and next steps with clarity.'}
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
                <button
                  onClick={() => i18n.changeLanguage(isArabic ? 'en' : 'ar')}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/25 bg-white/12 px-4 py-3 text-sm font-bold text-white shadow-sm backdrop-blur transition hover:bg-white/18"
                >
                  <Globe className="h-4 w-4" />
                  <span>{isArabic ? 'العربية | EN' : 'AR | EN'}</span>
                </button>

                <div ref={menuRef} className="relative">
                  <button
                    onClick={() => setMenuOpen((value) => !value)}
                    className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/12 px-4 py-3 text-left text-white backdrop-blur transition-all hover:bg-white/15"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/14">
                      <UserCircle2 className="h-6 w-6" />
                    </div>
                    <div className="hidden sm:block">
                      <div className="text-sm font-black">{displayName}</div>
                      <div className="text-xs text-white/70">
                        {isArabic ? 'آخر نشاط: اليوم 10:45 صباحًا' : 'Last activity: Today 10:45 AM'}
                      </div>
                    </div>
                    <Menu className="h-5 w-5" />
                  </button>

                  {menuOpen && (
                    <div
                      className={`absolute mt-3 w-72 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,39,66,0.18)] z-50 ${
                        isArabic ? 'right-0' : 'left-0'
                      }`}
                    >
                      <div className="border-b border-slate-100 px-5 py-4">
                        <div className="text-base font-black" style={{ color: PALETTE.navy }}>
                          {displayName}
                        </div>
                        <div className="mt-1 text-sm text-slate-500">
                          {isArabic
                            ? `رقم الملف: ${caseData.clientFileNumber}`
                            : `File ID: ${caseData.clientFileNumber}`}
                        </div>
                      </div>

                      <div className="p-2">
                        {[
                          { key: 'profile', label: isArabic ? 'الملف الشخصي' : 'Profile', icon: UserCircle2 },
                          { key: 'update', label: isArabic ? 'تحديث البيانات' : 'Update Details', icon: UserPen },
                          { key: 'photo', label: isArabic ? 'تغيير صورة الحساب' : 'Change Photo', icon: Camera },
                        ].map((item) => {
                          const Icon = item.icon;
                          return (
                            <button
                              key={item.key}
                              className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                            >
                              <Icon className="h-4 w-4" style={{ color: PALETTE.green }} />
                              <span>{item.label}</span>
                            </button>
                          );
                        })}

                        {canSwitchAccount && (
                          <button className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
                            <RefreshCw className="h-4 w-4" style={{ color: PALETTE.green }} />
                            <span>{isArabic ? 'تبديل الحساب / الصلاحية' : 'Switch Account / Role'}</span>
                          </button>
                        )}

                        <button
                          onClick={logout}
                          className="mt-1 flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-rose-600 transition hover:bg-rose-50"
                        >
                          <LogOut className="h-4 w-4" />
                          <span>{isArabic ? 'تسجيل الخروج' : 'Log Out'}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="mt-5">
          <CaseHeader caseData={caseData} isArabic={isArabic} />
        </div>

        <div
          ref={contentRef}
          className="mt-8 grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-8"
        >
          <aside className="order-2 lg:order-1">
            <CaseSidebar
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
            />
          </aside>

          <main className="order-1 lg:order-2">
            <CaseMainPanel
              caseData={caseData}
              activeSection={activeSection}
            />
          </main>
        </div>
      </div>
    </div>
  );
};

export default CaseDetailsPage;
